import { CheckCircle2, Circle, CircleAlert, Clock, ShieldCheck, TriangleAlert } from "lucide-react";
import { formatDate } from "@/lib/api";
import { ISSUERS, STATE_TONE, daysLeft } from "./helpers";

export function StateBadge({ d, testid }) {
  return <span className={`inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-medium ${STATE_TONE[d.state] || STATE_TONE.dns}`} data-testid={testid}>{d.state_label}</span>;
}

export function DomainSteps({ d, testid }) {
  return (
    <ol className="grid gap-2 sm:grid-cols-4" data-testid={testid}>
      {d.steps.map((s, i) => (
        <li key={s.key} className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs ${s.ok ? "border-emerald-200 bg-emerald-50/60 text-emerald-900" : "border-slate-200 text-slate-500"}`} data-testid={`${testid}-${s.key}`}>
          {s.ok ? <CheckCircle2 size={15} className="shrink-0 text-emerald-600" /> : <Circle size={15} className="shrink-0 text-slate-300" />}<span><b className="font-medium">{i + 1}.</b> {s.label}</span>
        </li>
      ))}
    </ol>
  );
}

export function DomainProblems({ d, testid }) {
  const items = [...d.problems.map((p) => [p.code, p.message, d.state === "problem" ? "error" : "info"]),
    ...(d.check_error ? [["check_error", `Ultimo controllo non riuscito: ${d.check_error}`, "error"]] : [])];
  if (!items.length) return null;
  return (
    <ul className="space-y-1.5" data-testid={testid}>
      {items.map(([code, msg, tone]) => (
        <li key={code} className={`flex items-start gap-2 rounded-lg px-3 py-2 text-sm ${tone === "error" ? "bg-red-50 text-red-800" : code === "switch" ? "bg-amber-50 text-amber-900" : "bg-slate-50 text-slate-700"}`} data-testid={`${testid}-${code}`}>
          {tone === "error" ? <CircleAlert size={15} className="mt-0.5 shrink-0" /> : <TriangleAlert size={15} className="mt-0.5 shrink-0 text-amber-600" />}{msg}
        </li>
      ))}
    </ul>
  );
}

export function SslLine({ d, testid }) {
  const s = d.ssl;
  return (
    <p className="flex flex-wrap items-center gap-x-1.5 text-xs text-slate-600" data-testid={testid}>
      <ShieldCheck size={14} className={s.status === "active" ? "text-emerald-600" : "text-slate-400"} />Certificato HTTPS: <b className="font-medium text-slate-800">{s.label || "In preparazione"}</b>
      {s.status === "active" && s.expires_on && <span>· rinnovo automatico, scade il {formatDate(s.expires_on)}</span>}
      {s.status === "active" && s.issuer && <span>· {ISSUERS[String(s.issuer).toLowerCase()] || s.issuer}</span>}
    </p>
  );
}

export function RemovalNote({ d, testid }) {
  if (!d.removal_at) return null;
  const n = daysLeft(d.removal_at);
  return (
    <p className="flex items-start gap-1.5 text-xs text-amber-800" data-testid={testid}>
      <Clock size={13} className="mt-0.5 shrink-0" />Se non viene verificato, il dominio sarà scollegato automaticamente il {formatDate(d.removal_at)} ({n === 1 ? "tra 1 giorno" : `tra ${n} giorni`}) per non occupare i 100 domini gratuiti.
    </p>
  );
}

export function VariantNote({ d, testid }) {
  const v = d.variant;
  if (!v || v.method !== "registrar") return null;
  const state = v.ok ? ["text-emerald-700", `Verificato (${v.detail})`] : v.checked_at ? ["text-amber-800", `Non ancora attivo: ${v.detail}`]
    : ["text-slate-500", d.mode === "emulator" ? "Si verifica solo con Cloudflare reale" : "Verifica al prossimo controllo"];
  return (
    <p className="text-xs text-slate-600" data-testid={testid}>Indirizzo senza www <b className="font-mono font-medium text-slate-800">{v.host}</b>: inoltro permanente (301) presso il registrar verso https://{d.host} · <span className={state[0]} data-testid={`${testid}-state`}>{state[1]}</span></p>
  );
}
